import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { catalogApiService } from '@/services/catalogApi.service';
import { queueApiService } from '@/services/queueApi.service';
import { ConfiguracaoSolucao } from '@/lib/fakeApi';
import { useCatalogWebSocket } from '@/hooks/useCatalogWebSocket';
import HighlightQueueCard from './HighlightQueueCard';
import ScheduledQueueCard from './ScheduledQueueCard';
import { useCorDoEvento } from '@/components/ds/IlhaEscura';

interface CatalogViewProps {
  eventId: string;
  storedToken: string;
}

const DEFAULT_CONFIG: ConfiguracaoSolucao = {
  id: 'default',
  corPrincipal: '#4369F5',
  corTexto: '#FFFFFF',
  logo: '/logo.png',
  imagemFundo: '/idVIsual.webp',
  termosUso: '',
  modoAgendamento: false,
};

export default function CatalogView({ eventId, storedToken }: CatalogViewProps) {
  const navigate = useNavigate();

  const { data: catalogData, isLoading } = useQuery({
    queryKey: ['catalog', storedToken],
    queryFn: () => catalogApiService.getPublicCatalog(storedToken),
    refetchInterval: 30000,
    enabled: !!storedToken,
  });

  const { data: branding } = useQuery({
    queryKey: ['branding', eventId],
    queryFn: () => queueApiService.getConfiguracaoSolucao(eventId),
    enabled: !!eventId,
  });

  const config = branding ?? DEFAULT_CONFIG;
  useCorDoEvento(config.corPrincipal);
  const onJoin = (queueId: string) => navigate(`/usuario?queueId=${queueId}`);

  const queueIds = [
    catalogData?.highlight?.queueId,
    ...(catalogData?.scheduledQueues ?? []).map(q => q.queueId),
  ].filter(Boolean) as string[];

  const { queueUpdates } = useCatalogWebSocket(queueIds);

  const mergeQueue = <T extends { queueId: string; status: string; currentSize: number; estimatedWait: number; maxSize: number | null; isFull: boolean }>(
    queue: T
  ): T => {
    const rt = queueUpdates[queue.queueId];
    if (!rt) return queue;
    const merged = {
      ...queue,
      ...(rt.status !== undefined && { status: rt.status }),
      ...(rt.currentSize !== undefined && { currentSize: rt.currentSize }),
      ...(rt.estimatedWait !== undefined && { estimatedWait: rt.estimatedWait }),
    };
    if (rt.currentSize !== undefined && merged.maxSize !== null) {
      merged.isFull = rt.currentSize >= merged.maxSize;
    }
    return merged;
  };

  const isHidden = (q: { status: string; isFull: boolean }) =>
    q.status === 'completed' || q.status === 'inactive' || q.isFull;

  const highlight = catalogData?.highlight ? mergeQueue(catalogData.highlight) : null;
  const showHighlight = highlight !== null && !isHidden(highlight) ? highlight : null;

  const sortedScheduled = [...(catalogData?.scheduledQueues ?? [])]
    .map(mergeQueue)
    .filter(q => !isHidden(q))
    .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));

  const bgStyle = { backgroundImage: `url(${config.imagemFundo || '/idVIsual.webp'})` };

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat relative"
        style={bgStyle}
      >
        <div className="absolute inset-0 bg-scrim/40" />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-border" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={bgStyle}
    >
      <div className="absolute inset-0 bg-scrim/40" />

      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <div className="flex justify-center pt-8 pb-4 px-4">
          {config.logo && (
            <img src={config.logo} alt="Logo" className="h-12 object-contain" />
          )}
        </div>

        <div className="px-4 pb-8 max-w-xl mx-auto space-y-4">
          {/* Highlight queue */}
          {showHighlight && (
            <HighlightQueueCard queue={showHighlight} config={config} onJoin={onJoin} />
          )}

          {/* Scheduled queues */}
          {sortedScheduled.length > 0 && (
            <div>
              <h2
                className="text-base font-semibold mb-3 opacity-90"
                style={{ color: config.corTexto }}
              >
                Filas Agendadas
              </h2>
              <div className="space-y-3">
                {sortedScheduled.map((queue) => (
                  <ScheduledQueueCard
                    key={queue.queueId}
                    queue={queue}
                    config={config}
                    onJoin={onJoin}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!showHighlight && sortedScheduled.length === 0 && (
            <div
              className="rounded-2xl border border-border bg-scrim/35 backdrop-blur-md shadow-2xl p-8 text-center"
              style={{ color: config.corTexto }}
            >
              <p className="opacity-80">Nenhuma fila disponível no momento.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
